// Pure logic for the PESTLE & SWOT panel — Story #6837 Stream 8c.
//
//  • switchPestleCountry: the country switch FIRST runs the pestle step for the new
//    country and only on `done` lets the caller change country (which fetches the new
//    query key). Every failure keeps the previous country and returns a message.
//  • cellSignal: the one pill a PESTLE cell shows, from its items' signals.
//  • owner overlay helpers for the SWOT Edit mode (PUT swot-overlay REPLACES the list).
import { postMarketAnalyseStep } from '../../services/api'
import { COUNTRY_LABELS } from '../../types/market'
import type {
  MarketAnalyseRequest, MarketAnalyseResult, MarketSwot, MarketSwotEntry, PestleItem, PestleSignal,
  SwotOverlayItem, SwotQuadrant,
} from '../../types/market'
import { SWOT_QUADRANTS } from '../../types/market'

export const MAX_OVERLAY_ITEMS = 40
export const MAX_OVERLAY_TEXT = 500

export interface PestleSwitchDeps {
  postMarketAnalyseStep: (projectId: string, body: MarketAnalyseRequest) => Promise<MarketAnalyseResult>
}

// Resolved at call time so screens whose tests stub services/api with an explicit
// factory can import this module without ever switching a country.
const realDeps: PestleSwitchDeps = {
  postMarketAnalyseStep: (projectId, body) => postMarketAnalyseStep(projectId, body),
}

export type SwitchResult = { ok: true } | { ok: false; message: string }

const labelOf = (c: string) => (COUNTRY_LABELS as Record<string, string>)[c] ?? c

type HttpErr = { response?: { status?: number; data?: { error?: unknown; retry_after?: unknown } }; code?: string; message?: string }

function failureMessage(err: unknown, country: string): string {
  const e = err as HttpErr | null | undefined
  const status = e?.response?.status
  const body = e?.response?.data ?? {}
  if (status === 429) {
    const ra = typeof body.retry_after === 'number' ? ` — try again in ${body.retry_after} s` : ''
    return `Too many market analyses right now${ra}. Still showing the previous country.`
  }
  if (status === 409) return `The PESTLE for ${labelOf(country)} could not start yet (an earlier step is missing). Still showing the previous country.`
  if (status === 504 || e?.code === 'ECONNABORTED' || e?.code === 'ETIMEDOUT' || /timeout|status code 504/i.test(e?.message ?? '')) {
    return `The PESTLE for ${labelOf(country)} is taking longer than expected — try again in a minute. Still showing the previous country.`
  }
  const text = typeof body.error === 'string' && body.error ? body.error : e?.message || 'The PESTLE could not be generated.'
  return `${text} Still showing the previous country.`
}

/** Run the pestle step for `country` (never forced: a fresh row costs nothing). */
export async function switchPestleCountry(
  projectId: string,
  country: string,
  deps: PestleSwitchDeps = realDeps,
): Promise<SwitchResult> {
  try {
    const res = await deps.postMarketAnalyseStep(projectId, { step: 'pestle', country })
    if (res.status === 'done') return { ok: true }
    if (res.status === 'insufficient_basis') {
      return { ok: false, message: `There is not enough information to produce a PESTLE for ${labelOf(country)}. Still showing the previous country.` }
    }
    return {
      ok: false,
      message: res.status === 'timeout'
        ? `The PESTLE for ${labelOf(country)} timed out — try again. Still showing the previous country.`
        : `The PESTLE for ${labelOf(country)} failed — try again. Still showing the previous country.`,
    }
  } catch (err) {
    return { ok: false, message: failureMessage(err, country) }
  }
}

export type CellSignal = PestleSignal | 'contested'

/** One signal → that signal; supportive AND tightening together → contested; anything else mixed. */
export function cellSignal(items: PestleItem[] | undefined): CellSignal | null {
  if (!items || items.length === 0) return null
  const set = new Set(items.map((i) => i.signal))
  if (set.size === 1) return [...set][0]
  return set.has('supportive') && set.has('tightening') ? 'contested' : 'mixed'
}

export const entryText = (e: MarketSwotEntry): string => (typeof e === 'string' ? e : e.text)
export const isOwnerEntry = (e: MarketSwotEntry): boolean => typeof e !== 'string' && e.source === 'owner'

/** The owner's overlay items as the PUT wants them back. */
export function ownerOverlay(swot: MarketSwot | null): SwotOverlayItem[] {
  if (!swot) return []
  const out: SwotOverlayItem[] = []
  for (const quadrant of SWOT_QUADRANTS) {
    for (const e of swot[quadrant]) if (isOwnerEntry(e)) out.push({ quadrant, text: entryText(e) })
  }
  return out
}

/** Null when the overlay is acceptable to the API (≤40 items, each 1–500 characters of text). */
export function overlayError(items: SwotOverlayItem[]): string | null {
  if (items.length > MAX_OVERLAY_ITEMS) return `At most ${MAX_OVERLAY_ITEMS} of your own items can be kept.`
  for (const i of items) {
    const t = i.text.trim()
    if (t.length === 0) return 'An item cannot be empty.'
    if (t.length > MAX_OVERLAY_TEXT) return `Each item can be at most ${MAX_OVERLAY_TEXT} characters.`
  }
  return null
}

export const addOverlayItem = (items: SwotOverlayItem[], quadrant: SwotQuadrant, text: string): SwotOverlayItem[] => {
  const t = text.trim()
  return t && !items.some((i) => i.quadrant === quadrant && i.text === t) ? [...items, { quadrant, text: t }] : items
}

export const removeOverlayItem = (items: SwotOverlayItem[], quadrant: SwotQuadrant, text: string): SwotOverlayItem[] =>
  items.filter((i) => !(i.quadrant === quadrant && i.text === text))
